import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { LandingDemoPlayer } from '@/features/demos/LandingDemoPlayer'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.mejor-herramienta-hosteleria-independiente-2026.${f}`

const REF = {
    hip: {
        href: 'https://www.expohip.com/en/future-gastronomy-hospitality-startup-forum/',
        domain: 'expohip.com',
        label: 'HIP · Future Gastronomy Startup Forum',
    },
    infohoreca: {
        href: 'https://www.infohoreca.com/noticias/20250606/numero-establecimientos-hosteleria-tardeo-fastfood-lujo',
        domain: 'infohoreca.com',
        label: 'InfoHoreca · UVE Data Market',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

function TipiLink({ children }: { children?: ReactNode }) {
    const { t } = useTranslation()
    return (
        <LocaleLink to='/' className={article.link}>
            {children ?? t(k('tipiLink'), 'Tipi')}
        </LocaleLink>
    )
}

export function MejorHerramientaHosteleriaArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo de la mejor herramienta para hostelería independiente y me gustaría ver si Tipi encaja con mi local.',
        ),
    )

    const weekActions: ReactNode[] = [
        <>
            <strong>{t(k('weekActions.1.strong'), 'Coge tu facturación anual y multiplícala por 0,01.')}</strong>{' '}
            {t(
                k('weekActions.1.after'),
                'Ese es tu ahorro realista al bajar un punto. Cualquier coste anual por encima de esa cifra es dinero perdido.',
            )}
        </>,
        <>
            <strong>{t(k('weekActions.2.strong'), 'Mira el último recibo del programa que ya tienes.')}</strong>{' '}
            {t(k('weekActions.2.after'), 'Si no has entrado este mes, ya tienes tu respuesta.')}
        </>,
        <>
            <strong>{t(k('weekActions.3.strong'), 'Escandalla tus cinco platos más vendidos.')}</strong>{' '}
            {t(k('weekActions.3.after'), 'Solo cinco, a mano si hace falta.')}
        </>,
        <>
            <strong>{t(k('weekActions.4.strong'), 'Cuando pidas presupuesto, tres preguntas:')}</strong>{' '}
            {t(
                k('weekActions.4.after'),
                '¿cuánto es la cuota de implementación? ¿Qué módulos hay que sumar para tener control de costes? ¿Cuántas horas mías hacen falta hasta que esto me sirva?',
            )}
        </>,
    ]

    const sources: Array<{ label: string; href?: string }> = [
        {
            label: t(k('sources.1'), 'HIP – Horeca Professional Expo, Future Gastronomy Startup Forum'),
            href: REF.hip.href,
        },
        {
            label: t(k('sources.2'), 'GOe – Gastronomy Open Ecosystem, Basque Culinary Center'),
        },
        {
            label: t(k('sources.3'), 'InfoHoreca, informe UVE Data Market Horeca 2025'),
            href: REF.infohoreca.href,
        },
        {
            label: t(
                k('sources.4'),
                'Páginas públicas de tarifas de Haddock, tspoonlab y Yurest (consultadas el 4 de agosto de 2026)',
            ),
        },
        {
            label: t(k('sources.5'), 'Hostelería de España, Anuario de la Hostelería Española'),
            href: REF.anuario.href,
        },
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(
                                k('dek.before56000'),
                                'Con los precios que publican las propias empresas, una herramienta de control de costes necesita que factures al menos ',
                            )}
                            <strong>{t(k('dek.minTipi'), '60.700 €')}</strong>
                            {t(
                                k('dek.after56000'),
                                ' al año para pagarse sola. Con otras, la cifra sube a 190.800 € o a 246.000 €. Nadie te lo dice antes de firmar, y la cuota de implementación, que casi todas cobran, no aparece anunciada en ningún sitio. Aquí está la cuenta entera.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Albarán y móvil con gráfica de costes sobre la encimera de una cocina profesional',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <div className={article.proseIntro}>
                    <p className={article.p}>
                        {t(
                            k('intro.1'),
                            'Te llamó un comercial. Te enseñó una pantalla llena de gráficos y te dijo que ibas a saber por fin cuánto ganas con cada plato.',
                        )}
                    </p>
                    <p className={article.p}>
                        {t(
                            k('intro.2'),
                            'Firmaste. Entraste tres veces en enero, una en febrero, y desde entonces pagas todos los meses un recibo de algo que no abres.',
                        )}
                    </p>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(k('intro.3'), 'No es culpa tuya. Y las cuentas explican bastante bien por qué pasa.')}
                    </p>
                </div>

                <section id='cuanto-cuesta-cada-herramienta'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuantoCuesta'), '¿Cuánto cuesta cada herramienta al año?')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('costes.intro'), 'Precios publicados por las propias empresas, sin IVA.')}
                        </p>
                    </div>
                    <div className={article.tableWrap}>
                        <table className={article.table}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.cost.col1'), 'Herramienta')}</th>
                                    <th scope='col'>{t(k('table.cost.col2'), 'Coste anual')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <TipiLink />
                                    </td>
                                    <td>
                                        {t(k('table.cost.tipi.before'), '39 €/mes + 139 € activación = ')}
                                        <strong>{t(k('table.cost.tipi.amount'), '607 €')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.cost.haddock'), 'Haddock (Básico)')}</td>
                                    <td>
                                        {t(k('table.cost.haddock.before'), '159 €/mes = ')}
                                        <strong>{t(k('table.cost.haddock.amount'), '1.908 €')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.cost.tspoonlab'), 'tspoonlab (Back + Fast)')}</td>
                                    <td>
                                        {t(k('table.cost.tspoonlab.before'), '205 €/mes = ')}
                                        <strong>{t(k('table.cost.tspoonlab.amount'), '2.460 €')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.cost.yurest'), 'Yurest')}</td>
                                    <td>{t(k('table.cost.yurest.value'), 'No publica precio')}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('costes.outro'),
                                'Ninguna de esas cifras, salvo la nuestra, incluye la puesta en marcha.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='cuota-implementacion'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.cuota'), 'La cuota que existe pero no tiene precio')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(k('cuota.p1.before'), 'En su página de precios, Haddock dedica un apartado a la ')}
                            <strong>
                                {t(
                                    k('cuota.p1.strong'),
                                    '"Cuota de Implementación personalizada para ti con cada plan"',
                                )}
                            </strong>
                            {t(k('cuota.p2'), '. Reconocen el coste de puesta en marcha. Lo que no dicen es cuánto.')}
                        </p>
                        <p className={article.p}>
                            <strong>
                                {t(
                                    k('cuota.p3.strong'),
                                    'Poner en marcha una de estas herramientas son entre 20 y 40 horas',
                                )}
                            </strong>{' '}
                            {t(
                                k('cuota.p3.after'),
                                'de meter proveedores, artículos, precios y recetas: o lo haces tú, o te lo cobran.',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('cuota.p4.before'), 'Cuando pidas presupuesto, la primera pregunta: ')}
                            <strong>{t(k('cuota.p4.strong'), '¿cuánto es la cuota de implementación?')}</strong>{' '}
                            {t(k('cuota.p4.after'), 'Rara vez viene junto al precio mensual.')}
                        </p>
                    </div>
                </section>

                <section id='tspoonlab-desde-95'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.tspoonlab95'), 'El "desde 95 €" de tspoonlab no incluye el control de costes')}
                    </h2>
                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('tspoon.p1.before'), 'tspoonlab anuncia su plan Professional ')}
                            <strong>{t(k('tspoon.p1.strong'), 'desde 95 € al mes')}</strong>
                            {t(
                                k('tspoon.p1.after'),
                                '. Ese plan digitaliza albaranes; el gestor de compras, inventarios, mermas y coste por elaboración van en tspoonFast: ',
                            )}
                            <strong>{t(k('tspoon.p2.price'), '110 € más al mes')}</strong>
                            {t(k('tspoon.p3.before'), '. Control de costes de verdad: ')}
                            <strong>{t(k('tspoon.p3.strong'), '205 € al mes')}</strong>
                            {t(k('tspoon.note'), ' (lectura nuestra del reparto de funciones en su web).')}
                        </p>
                    </div>
                </section>

                <aside className={article.breakout} aria-label={t(k('breakout.aria'), 'La cuenta que nadie te hace')}>
                    <div className={article.breakCopy}>
                        <p className={article.breakEyebrow}>
                            {t(k('breakout.eyebrow'), 'La cuenta que nadie te hace')}
                        </p>
                        <p className={article.breakLead}>
                            {t(
                                k('breakout.lead'),
                                '60.700 €, 190.800 € o 246.000 € de facturación mínima para que la herramienta se pague sola.',
                            )}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('breakout.p'),
                                'Bajar un punto de food cost te ahorra el 1% de lo que facturas. Divide el coste anual entre ese 1% y sabrás si compensa.',
                            )}
                        </p>
                    </div>
                </aside>

                <section id='facturacion-minima'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.facturacion'), 'La facturación mínima para que la herramienta se pague sola')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('minimo.p1'),
                                'Food cost: de cada 10 € del plato, lo que te costó el género. Si fueron 3,30 €, es un 33%.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>
                                {t(
                                    k('minimo.p2.strong'),
                                    'Bajar un punto de food cost te ahorra el 1% de lo que facturas.',
                                )}
                            </strong>{' '}
                            {t(
                                k('minimo.p2.after'),
                                'Si haces 300.000 €, son 3.000 € al año. Divide el coste anual entre ese 1% y sabrás si compensa.',
                            )}
                        </p>
                    </div>
                    <div className={article.tableWrap}>
                        <table className={article.table}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.min.col1'), 'Herramienta')}</th>
                                    <th scope='col'>{t(k('table.min.col2'), 'Facturación mínima')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <TipiLink />
                                    </td>
                                    <td>
                                        <strong>{t(k('table.min.tipi.amount'), '60.700 €')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.min.haddock'), 'Haddock (Básico)')}</td>
                                    <td>
                                        <strong>{t(k('table.min.haddock.amount'), '190.800 €')}</strong>
                                    </td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.min.tspoonlab'), 'tspoonlab (Back + Fast)')}</td>
                                    <td>
                                        <strong>{t(k('table.min.tspoonlab.amount'), '246.000 €')}</strong>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className={article.prose}>
                        <p className={cn(article.pItalic, article.pGapMd)}>
                            {t(
                                k('minimo.note'),
                                '(Cálculo propio: coste anual dividido entre el 1% de facturación. La cifra de Tipi incluye nuestra activación; las otras dos no incluyen la cuota de implementación, que no es pública, así que su mínimo real será algo mayor.)',
                            )}
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(k('minimo.outro.before'), 'Traducido: ')}
                            <strong>
                                {t(
                                    k('minimo.outro.strong'),
                                    'si tu bar factura 200.000 €, los dos planes de tspoonlab para bajar un punto te hacen perder dinero.',
                                )}
                            </strong>{' '}
                            {t(k('minimo.outro.after'), 'No por mala herramienta: no está pensada para tu tamaño.')}
                        </p>
                    </div>
                </section>

                <section id='que-herramienta-conviene'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.conviene'), '¿Qué herramienta le conviene a cada uno?')}
                    </h2>
                    <div className={article.prose}>
                        <p className={article.p}>
                            {t(
                                k('justas.p3.before'),
                                'Haddock, tspoonlab y Yurest cobran por módulos que existen y funcionan (documentos, producción, personal, finanzas). La pregunta no es si valen su precio, sino ',
                            )}
                            <strong>{t(k('justas.p3.strong'), 'si tú las necesitas')}</strong>
                            {t(
                                k('justas.p4'),
                                '. Si una herramienta más potente te hace bajar dos puntos en vez de uno, la cuenta se da la vuelta.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('conviene.tspoonlab.strong'), 'tspoonlab')}</strong>
                            {t(
                                k('conviene.tspoonlab.after'),
                                ': cocina de producción, carta muy elaborada o trazabilidad.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('conviene.yurest.strong'), 'Yurest')}</strong>
                            {t(
                                k('conviene.yurest.after'),
                                ': varios locales y compras, personal y finanzas centralizados.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('conviene.haddock.strong'), 'Haddock')}</strong>
                            {t(
                                k('conviene.haddock.after'),
                                ': mucha factura, conciliación bancaria y variaciones de proveedor.',
                            )}
                        </p>
                        <p className={article.p}>
                            <strong>{t(k('conviene.tipi.strong'), 'Tipi')}</strong>
                            {t(
                                k('conviene.tipi.after'),
                                ': sí, somos nosotros. Para el dueño que cocina o está en barra: dictas la receta, fotografías el albarán, sin integrarlo con nada. ',
                            )}
                            <strong>{t(k('tipiCost.p1.strong'), '39 € al mes, más 139 € de activación.')}</strong>{' '}
                            {t(k('tipiCost.p1.after'), 'Primer año 607 €; después 468 €; anual 420 €.')}
                        </p>
                        <p className={article.p}>
                            {t(
                                k('video.intro'),
                                'Así se ve en el móvil: recetario, foto al albarán, gasto por proveedor e inventario.',
                            )}
                        </p>
                        <LandingDemoPlayer demoId='product-tour' variant='article' />
                        <p className={article.p}>
                            {t(k('conviene.premio.before'), 'Ese enfoque fue premiado el ')}
                            <strong className={article.cite}>
                                {t(
                                    k('conviene.premio.date'),
                                    '17 de febrero de 2026 en HIP – Horeca Professional Expo (IFEMA Madrid)',
                                )}
                                <SourceChip source='hip' />
                            </strong>
                            {t(
                                k('conviene.premio.mid'),
                                ' (Future Gastronomy Startup Forum de GOe y Basque Culinary Center). Detalle honesto: ',
                            )}
                            <strong>{t(k('conviene.premio.strong'), 'ese premio no lleva dinero.')}</strong>
                        </p>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('conviene.outro'),
                                'No somos la mejor opción con cocina central, más de tres locales o si buscas un TPV.',
                            )}
                        </p>
                    </div>
                </section>

                <section id='esta-semana'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.estaSemana'), 'Cómo elegir herramienta sin firmar a ciegas')}
                    </h2>
                    <div className={article.prose}>
                        <NewsArticleNumList items={weekActions} />
                    </div>
                </section>

                <section id='pendientes'>
                    <h2 className={cn(article.h2, article.h2Spaced)}>
                        {t(k('h2.pendientes'), 'Precios no públicos, implementación y lo que Tipi no hace')}
                    </h2>
                    <div className={article.prose}>
                        <ul className={article.dashListGap2xl}>
                            <li className={article.dashItem}>
                                <strong>{t(k('pendientes.1.strong'), 'Yurest no publica precio')}</strong>
                                {t(
                                    k('pendientes.1.after'),
                                    ', así que no podemos incluirlo en las tablas. Si te pasan presupuesto, aplícale tú la división.',
                                )}
                            </li>
                            <li className={article.dashItem}>
                                <strong>
                                    {t(k('pendientes.2.strong'), 'Las cuotas de implementación no son públicas')}
                                </strong>
                                {t(
                                    k('pendientes.2.after'),
                                    ' en ninguna de las tres. Sabemos que Haddock la cobra porque lo dice en su web; el importe, no.',
                                )}
                            </li>
                            <li className={article.dashItem}>
                                <strong>{t(k('pendientes.3.strong'), 'Los precios cambian.')}</strong>{' '}
                                {t(
                                    k('pendientes.3.after'),
                                    'Estos son los publicados a 4 de agosto de 2026. Haddock especifica que los suyos no incluyen IVA.',
                                )}
                            </li>
                            <li className={article.dashItem}>
                                <strong>
                                    {t(
                                        k('pendientes.4.strong'),
                                        'La lectura de los dos planes de tspoonlab es nuestra',
                                    )}
                                </strong>
                                {t(k('pendientes.4.after'), ', deducida del reparto de funciones publicado en su web.')}
                            </li>
                            <li className={article.dashItem}>
                                <strong>{t(k('pendientes.5.strong'), 'Lo que Tipi no hace todavía:')}</strong>{' '}
                                {t(
                                    k('pendientes.5.after'),
                                    'pedidos a proveedores, recepción de materia prima, órdenes de producción y trazabilidad de lotes. Está en el plan, sin fecha pública. No contrates hoy contando con ello.',
                                )}
                            </li>
                        </ul>
                    </div>
                </section>

                <div className={article.prose}>
                    <p className={cn(article.p, article.pGap2xl)}>
                        {t(
                            k('closing.p1'),
                            'Si el local factura bien y a fin de mes no queda nada, y no sabes por dónde se escapa, cuéntanos cuánto facturas y aplicamos juntos la cuenta mínima: la que nadie te hace en la demo.',
                        )}
                    </p>
                </div>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Tu herramienta se paga sola con tu facturación?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanos cuánto factura tu local y te ayudamos a ver si Tipi encaja, o si te conviene otra cosa.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.label}>
                                {source.href ? (
                                    <a
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {source.label}
                                    </a>
                                ) : (
                                    source.label
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
